import React, { useEffect, useRef } from "react";
// The light player rather than the full one: these files are ellipses and
// gradients — no expressions, no effects, no images, no text — and the light
// build is the same renderer with all of that left out. It is roughly a third
// of the size, which matters for three decorative loops on one card.
import lottie, { AnimationItem } from "lottie-web/build/player/lottie_light";

/** The disc the step's mark is drawn at, which the rings have to clear. */
const DISC = 48;

/**
 * How far past the edge of that disc the widest ring reaches, at the moment it
 * is widest. The one number here worth tuning.
 *
 * 12px, because that is the gap between the disc and the words beside it — see
 * `ROW`'s `gap-3` in `SetupChecklist`. A ring that ran further would sweep
 * across the step's own title four times a loop.
 */
const REACH = 12;

/**
 * The box the animation is drawn into.
 *
 * It cannot be the size of the disc, which is what it looks like it should be:
 * all three files are 512 units square, and their rings peak at 243 of those —
 * under half the frame. Drawn at 48px the whole ripple lands inside 23px and
 * disappears behind the mark on top of it, which reads as no animation at all.
 *
 * So the box is scaled by what has to be true of the picture instead: the
 * widest ring lands on the disc plus the reach either side of it.
 */
const RING_PEAK = 243;
const COMP = 512;
const BOX = Math.round(((DISC + REACH * 2) / RING_PEAK) * COMP);

interface StepRippleProps {
  /** The animation, imported from its JSON. */
  data: unknown;
}

/**
 * The halo behind the step the reader is on.
 *
 * The files are rings and a drop and nothing else — no crown, no briefcase, no
 * people — so this sits *behind* the step's mark rather than in place of it.
 * On its own it would be an animated step with no icon in it.
 *
 * Decorative and nothing more: `aria-hidden`, and it carries no label, because
 * which step is live is already said in words by the note under the title.
 */
const StepRipple: React.FC<StepRippleProps> = ({ data }) => {
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const host = box.current;
    if (!host) return;

    // Nobody who has asked their system for less movement gets a four-second
    // loop pulsing under their cursor. They keep the mark alone, which is what
    // the other two steps show anyway — so the row stays whole rather than
    // losing anything.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let animation: AnimationItem | undefined;
    try {
      animation = lottie.loadAnimation({
        container: host,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: data,
      });
    } catch (error) {
      // A halo is the last thing on this card worth breaking a dashboard for.
      console.error("step ripple failed to load:", error);
      return;
    }

    return () => animation?.destroy();
  }, [data]);

  return (
    <span
      ref={box}
      aria-hidden
      // Centred on the disc and behind it, and far wider than it — see `BOX`.
      // The size is worked out rather than written down, so it stays inline
      // rather than in a class Tailwind would have to be told about.
      style={{ width: BOX, height: BOX }}
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    />
  );
};

export default StepRipple;
